import { zodResolver } from '@hookform/resolvers/zod'
import { SaveOutlined } from '@mui/icons-material'
import CloseIcon from '@mui/icons-material/Close'
import DeleteOutlinedIcon from '@mui/icons-material/DeleteOutlined'
import { Button, Divider, Stack } from '@mui/material'
import { DateTime } from 'luxon'
import { useState } from 'react'
import { FormProvider, useForm, useWatch } from 'react-hook-form'
import { useTranslation } from 'react-i18next'

import { ZONED_TODAY } from '@/constants/dateTime'
import { useAlertContext } from '@/contexts/AlertContext'
import { useAnalytics } from '@/features/googleAnalytics/hooks/useAnalytics'
import { UnavailabilityResourceSelectionSection } from '@/features/site/components/availability/form/resourceLevelUnavailaibility/UnavailabilityResourceSelectionSection'
import { DeleteRestrictionConfirmationDialog } from '@/features/site/components/availability/restriction/DeleteRestrictionConfirmationDialog'
import { ResourcelevelRestrictionNonRepeatingDatesSection } from '@/features/site/components/availability/restriction/form/resourceLevelUnavailaibility/ResourceLevelRestrictionNonRepeatingDatesSection'
import { ResourceLevelRestrictionRepeatingDatesSection } from '@/features/site/components/availability/restriction/form/resourceLevelUnavailaibility/ResourceLevelRestrictionRepeatingDatesSection'
import { ResourceLevelRestrictionStatusSection } from '@/features/site/components/availability/restriction/form/resourceLevelUnavailaibility/ResourceLevelRestrictionStatusSection'
import { RestrictionConfigurationInfoSection } from '@/features/site/components/availability/restriction/form/RestrictionConfigurationInfoSection'
import { RestrictionTypeSection } from '@/features/site/components/availability/restriction/form/RestrictionTypeSection'
import { useCreateResourceLevelRestrictionMutation } from '@/features/site/hooks/resourceLevelUnavailaibility/useCreateResourceLevelRestrictionMutation'
import { useDeleteResourceLevelRestrictionMutation } from '@/features/site/hooks/resourceLevelUnavailaibility/useDeleteResourceLevelRestrictionMutation'
import { useEditResourceLevelRestrictionMutation } from '@/features/site/hooks/resourceLevelUnavailaibility/useEditResourceLevelRestrictionMutation'
import type {
  CreateResourceLevelRestrictionRequest,
  EditResourceLevelRestrictionRequest,
} from '@/features/site/types/restriction'
import type { ResourceLevelRestrictionForm } from '@/features/site/types/restrictionForm'
import { getResourceLevelRestrictionFormSchema } from '@/features/site/types/restrictionForm'
import { AVAILABILITY_STATUSES } from '@/features/unavailability/types/availabilityStatus'
import type { ResourceLevelRestriction } from '@/features/unavailability/types/restriction'
import { convertToTimeZoneDateTime } from '@/utils/time'

interface ResourceLevelRestrictionDetailsFormProps {
  onClose: () => void
  customerTimeZone: string
  customerLocation: string
  customerUuid: string
  restriction?: ResourceLevelRestriction
}

export const ResourceLevelRestrictionDetailsForm = (props: ResourceLevelRestrictionDetailsFormProps) => {
  const { t } = useTranslation()
  const { onClose } = props
  const { pushAlert } = useAlertContext()
  const { sendAnalyticsEvent } = useAnalytics()

  const [deleteConfirmationDialogOpen, setDeleteConfirmationDialogOpen] = useState(false)

  const isDeleteAllowed = props.restriction?.status === AVAILABILITY_STATUSES.waiting.value

  const form = useForm<ResourceLevelRestrictionForm>({
    mode: 'onBlur',
    defaultValues: {
      id: props.restriction?.id ?? undefined,
      type: props.restriction?.type ?? 'non-repeating',
      startDate: props.restriction?.startDate
        ? convertToTimeZoneDateTime(props.restriction.startDate.toString(), props.customerTimeZone)
        : ZONED_TODAY(props.customerTimeZone).plus({ days: 1 }),
      endDate: props.restriction?.endDate
        ? convertToTimeZoneDateTime(props.restriction.endDate.toString(), props.customerTimeZone)
        : ZONED_TODAY(props.customerTimeZone).plus({ days: 1 }),
      startTime: props.restriction?.startTime
        ? DateTime.fromISO(props.restriction.startTime, { zone: props.customerTimeZone })
        : ZONED_TODAY(props.customerTimeZone).startOf('day').startOf('minute'),
      endTime: props.restriction?.endTime
        ? DateTime.fromISO(props.restriction.endTime, { zone: props.customerTimeZone })
        : ZONED_TODAY(props.customerTimeZone).endOf('day').startOf('minute'),
      allDay: false,
      affectedResources: props.restriction?.resources?.map((resource) => resource.id) ?? [],
      location: props.customerLocation,
      status: props.restriction?.status ?? undefined,
      enabled: props.restriction?.status !== 'deactivated',
    },
    resolver: zodResolver(getResourceLevelRestrictionFormSchema(t)),
  })

  useWatch({
    control: form.control,
  })

  const { createResourceLevelRestriction } = useCreateResourceLevelRestrictionMutation()
  const { editResourceLevelRestriction } = useEditResourceLevelRestrictionMutation()
  const { deleteResourceLevelRestriction } = useDeleteResourceLevelRestrictionMutation()

  function convertToRequest(
    formValues: ResourceLevelRestrictionForm,
  ): CreateResourceLevelRestrictionRequest | EditResourceLevelRestrictionRequest | null {
    const startDateString = formValues.startDate.toFormat('yyyy-MM-dd')
    const endDateString = formValues.endDate.toFormat('yyyy-MM-dd')

    if (!startDateString || !endDateString) {
      return null
    }

    // Form the base request (common for both create and edit)
    const result: CreateResourceLevelRestrictionRequest = {
      type: formValues.type,
      startDate: startDateString,
      endDate: endDateString,
      startTime: formValues.startTime.toFormat('HH:mm'),
      endTime: formValues.endTime.toFormat('HH:mm'),
      affectedResources: formValues.affectedResources.map((id) => ({ id })),
      location: formValues.location,
    }

    // if it's present (it's an edit request)
    if (formValues.id !== undefined) {
      return {
        ...result,
        id: formValues.id,
        enabled: formValues.enabled,
      } as EditResourceLevelRestrictionRequest
    }

    return result
  }

  const handleSubmit = async () => {
    const restrictionRequest = convertToRequest(form.getValues())

    if (!restrictionRequest) {
      form.trigger() // Trigger validation to show errors
      return
    }

    if (props.restriction) {
      await editResourceLevelRestriction({
        customerUuid: props.customerUuid,
        restriction: restrictionRequest as EditResourceLevelRestrictionRequest,
      })
      pushAlert({
        message: t('customer_details.tabs.availability.unavailability_form.edit_dialog.success.message'),
        severity: 'success',
      })
    } else {
      await createResourceLevelRestriction({ customerUuid: props.customerUuid, restriction: restrictionRequest }).then(
        () => {
          sendAnalyticsEvent({
            name: 'availability_created',
            dimensions: {
              customer_uuid: props.customerUuid,
              availability_type: 'resource_level_unavailability',
            },
          })
        },
      )
      pushAlert({
        message: t('customer_details.tabs.availability.unavailability_form.create_dialog.success.message'),
        severity: 'success',
      })
    }
    handleClose()
  }

  const handleClose = () => {
    onClose()
    form.reset()
  }

  const handleDelete = async () => {
    setDeleteConfirmationDialogOpen(false)
    if (props.restriction?.id && isDeleteAllowed) {
      await deleteResourceLevelRestriction({
        customerUuid: props.customerUuid,
        restrictionId: props.restriction.id,
      })
      pushAlert({
        message: t('customer_details.tabs.availability.unavailability_form.delete_dialog.success.message'),
        severity: 'success',
      })
    }
    handleClose()
  }

  const confirmDeleteOperation = () => {
    setDeleteConfirmationDialogOpen(true)
  }

  return (
    <>
      <Stack spacing={2}>
        <FormProvider {...form}>
          <RestrictionTypeSection />
          {form.watch('type') === 'non-repeating' ? (
            <ResourcelevelRestrictionNonRepeatingDatesSection timezone={props.customerTimeZone} />
          ) : (
            <ResourceLevelRestrictionRepeatingDatesSection timezone={props.customerTimeZone} />
          )}
          <UnavailabilityResourceSelectionSection siteUuid={props.customerUuid} />
          <Divider style={{ marginBottom: '16px', marginTop: '16px' }} />
          <ResourceLevelRestrictionStatusSection />
          <RestrictionConfigurationInfoSection customerTimeZone={props.customerTimeZone} />
        </FormProvider>
        <Stack direction="row" justifyContent="space-between" spacing={2}>
          <Stack direction="row" spacing={1}>
            <Button startIcon={<SaveOutlined />} variant={'contained'} onClick={form.handleSubmit(handleSubmit)}>
              {t('common.button.save')}
            </Button>
            <Button startIcon={<CloseIcon />} variant={'outlined'} onClick={handleClose}>
              {t('common.button.cancel')}
            </Button>
          </Stack>
          {isDeleteAllowed && (
            <Button
              color="error"
              startIcon={<DeleteOutlinedIcon />}
              variant={'outlined'}
              onClick={confirmDeleteOperation}
            >
              {t('common.button.delete')}
            </Button>
          )}
        </Stack>
      </Stack>
      <DeleteRestrictionConfirmationDialog
        open={deleteConfirmationDialogOpen}
        onClose={() => setDeleteConfirmationDialogOpen(false)}
        onConfirm={handleDelete}
      />
    </>
  )
}
